import { ShieldCheck } from "lucide-react";
import { SPECIAL_EVENT_FREE_ONLY_MESSAGE } from "@shared/registrationApproval";

export { SPECIAL_EVENT_FREE_ONLY_MESSAGE };

export const SPECIAL_EVENT_APPROVAL_LABEL = "Special event (manual approval)";
export const SPECIAL_EVENT_APPROVAL_HELP =
  "Registrations are held as pending until an admin approves them. The Zoom link stays hidden until a registration is approved.";

export type SpecialEventApprovalState = { isSpecialEvent: boolean; pendingReviewCount: number };

/** Why the toggle can't change right now, or null when it can. */
export function specialEventToggleLock(input: {
  approval: SpecialEventApprovalState;
  pricingMode: "free" | "paid_external" | "paid_internal" | undefined;
}): string | null {
  const { approval, pricingMode } = input;
  if (approval.isSpecialEvent && approval.pendingReviewCount > 0) {
    const count = approval.pendingReviewCount;
    return `${count} registration${count === 1 ? " is" : "s are"} awaiting review. Approve or decline ${count === 1 ? "it" : "them"} before turning off manual approval.`;
  }
  if (!approval.isSpecialEvent && pricingMode && pricingMode !== "free") {
    return SPECIAL_EVENT_FREE_ONLY_MESSAGE;
  }
  return null;
}

/** Why paid registration can't be added right now, or null when it can. */
export function specialEventPricingLock(isSpecialEvent: boolean): string | null {
  return isSpecialEvent ? SPECIAL_EVENT_FREE_ONLY_MESSAGE : null;
}

export default function SpecialEventApprovalControl({
  approval,
  value,
  onChange,
  pricingMode,
  readOnly = false,
}: {
  approval: SpecialEventApprovalState;
  value: boolean;
  onChange: (next: boolean) => void;
  pricingMode: "free" | "paid_external" | "paid_internal" | undefined;
  readOnly?: boolean;
}) {
  const lock = specialEventToggleLock({ approval, pricingMode });
  return (
    <div style={{ background: "rgba(167,139,250,.05)", border: "1px solid rgba(167,139,250,.2)", borderRadius: ".65rem", padding: ".95rem 1rem" }}>
      <label style={{ display: "flex", alignItems: "center", gap: ".55rem", cursor: readOnly || lock ? "not-allowed" : "pointer" }}>
        <input
          type="checkbox"
          checked={value}
          disabled={readOnly || Boolean(lock)}
          onChange={event => onChange(event.target.checked)}
          aria-describedby="special-event-approval-help"
        />
        <ShieldCheck size={15} style={{ color: "#C4B5FD" }} />
        <span style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".8rem", fontWeight: 700 }}>{SPECIAL_EVENT_APPROVAL_LABEL}</span>
      </label>
      <p id="special-event-approval-help" style={{ margin: ".5rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
        {SPECIAL_EVENT_APPROVAL_HELP}
      </p>
      {lock && !readOnly && (
        <p role="status" style={{ margin: ".5rem 0 0", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: ".74rem", lineHeight: 1.45 }}>{lock}</p>
      )}
    </div>
  );
}
